import {
  EMPLOYEE_DOCUMENT_TYPES,
  EMPLOYEE_LIST_STATUSES,
  EMPLOYEE_SORTS,
  type EmployeeDocumentType,
  type EmployeeListStatus,
  EXPIRY_BUCKETS,
  type ExpiryBucket,
  NATIONALITY_CLASSES,
  type NationalityClass,
} from '@sahab/contracts'
import { z } from 'zod'

export const PAGE_SIZES = [25, 50, 100] as const

const pageSize = z
  .number()
  .refine((n): n is (typeof PAGE_SIZES)[number] => (PAGE_SIZES as readonly number[]).includes(n))
  .optional()
  .catch(undefined)

const page = z.number().int().min(1).optional().catch(undefined)

// A hand-written `?q=99112045` arrives as a number (the router parses JSON): keep it as text.
const q = z.coerce.string().trim().max(200).optional().catch(undefined)

// ── employees ────────────────────────────────────────────────────────────────

type EmployeeSortField = (typeof EMPLOYEE_SORTS)[number]
export type EmployeeSort = EmployeeSortField | `-${EmployeeSortField}`

export function isEmployeeSort(value: string): value is EmployeeSort {
  const field = value.startsWith('-') ? value.slice(1) : value
  return (EMPLOYEE_SORTS as readonly string[]).includes(field)
}

/** The employee register's list state in the URL; an invalid value falls back to the default. */
export const employeeSearchSchema = z.object({
  page,
  pageSize,
  q,
  sort: z.string().refine(isEmployeeSort).optional().catch(undefined),
  /** Absent: CURRENT (everyone not ended), the API's default too. */
  status: z.enum(EMPLOYEE_LIST_STATUSES).optional().catch(undefined),
  nationality: z.enum(NATIONALITY_CLASSES).optional().catch(undefined),
  orgUnitId: z.uuid().optional().catch(undefined),
})
export type EmployeeSearch = z.infer<typeof employeeSearchSchema>

export interface EmployeeListParams {
  page: number
  pageSize: number
  sort: string
  q: string | undefined
  status: EmployeeListStatus
  nationality: NationalityClass | undefined
  orgUnitId: string | undefined
}

/** By number by default (the hiring order). */
export const EMPLOYEE_DEFAULT_SORT = 'number'

export function toEmployeeListQuery(search: EmployeeSearch): EmployeeListParams {
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? PAGE_SIZES[0],
    sort: search.sort ?? EMPLOYEE_DEFAULT_SORT,
    q: search.q || undefined,
    status: search.status ?? 'CURRENT',
    nationality: search.nationality,
    orgUnitId: search.orgUnitId,
  }
}

// ── the employee page ────────────────────────────────────────────────────────

export const EMPLOYEE_TABS = [
  'overview',
  'contract',
  'salary',
  'documents',
  'files',
  'payslips',
  'history',
] as const
export type EmployeeTab = (typeof EMPLOYEE_TABS)[number]

export const employeePageSearchSchema = z.object({
  tab: z.enum(EMPLOYEE_TABS).optional().catch(undefined),
})
export type EmployeePageSearch = z.infer<typeof employeePageSearchSchema>

// ── document expiry ──────────────────────────────────────────────────────────

export const documentSearchSchema = z.object({
  page,
  pageSize,
  q,
  /** Absent: D90 (everything expired or due within 90 days). */
  within: z.enum(EXPIRY_BUCKETS).optional().catch(undefined),
  /** `docType`, not `type`: links that keep the search of any page merge every route's keys. */
  docType: z.enum(EMPLOYEE_DOCUMENT_TYPES).optional().catch(undefined),
  orgUnitId: z.uuid().optional().catch(undefined),
})
export type DocumentSearch = z.infer<typeof documentSearchSchema>

export interface DocumentListParams {
  page: number
  pageSize: number
  q: string | undefined
  within: ExpiryBucket
  type: EmployeeDocumentType | undefined
  orgUnitId: string | undefined
}

export function toDocumentListQuery(search: DocumentSearch): DocumentListParams {
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? PAGE_SIZES[0],
    q: search.q || undefined,
    within: search.within ?? 'D90',
    type: search.docType,
    orgUnitId: search.orgUnitId,
  }
}

// ── pay components ───────────────────────────────────────────────────────────

export const payComponentSearchSchema = z.object({
  archived: z.boolean().optional().catch(undefined),
})
export type PayComponentSearch = z.infer<typeof payComponentSearchSchema>
